import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CampaignIcon from "@mui/icons-material/Campaign";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CircleIcon from "@mui/icons-material/Circle";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../axiosConfig";

const DAY_OPTIONS = [7, 30, 90];

const STATUS_META = {
  available: { label: "Available", color: "#16a34a", tone: "rgba(22,163,74,0.14)" },
  offline: { label: "Offline", color: "#64748b", tone: "rgba(100,116,139,0.16)" },
};

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatHours = (value) => `${Number(value || 0).toFixed(1)}h`;

const formatDateTime = (value) => {
  if (!value) return "No activity yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "No activity yet";

  return parsed.toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusMeta = (status) => STATUS_META[status] || STATUS_META.offline;

function SummaryCard({ title, value, subtitle, accent, icon }) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 4,
        height: "100%",
        background: `linear-gradient(145deg, ${accent}12, #ffffff)`,
        border: `1px solid ${accent}22`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${accent}16`,
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Chip size="small" label="Live" sx={{ fontWeight: 700 }} />
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.6 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

function BreakdownBars({ title, description, items, accent }) {
  const highest = Math.max(...(items?.map((item) => Number(item.quantity || item.events || 0)) || [0]));

  return (
    <Paper sx={{ p: 3, borderRadius: 4, height: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        {description}
      </Typography>
      {items?.length ? (
        <Stack spacing={2}>
          {items.map((item) => {
            const value = Number(item.quantity || item.events || 0);
            const share = highest > 0 ? Math.round((value / highest) * 100) : 0;

            return (
              <Box key={`${title}-${item.key}`}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {formatNumber(value)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={share}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    bgcolor: `${accent}16`,
                    "& .MuiLinearProgress-bar": {
                      bgcolor: accent,
                      borderRadius: 999,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Alert severity="info">No tracked activity landed in this breakdown yet.</Alert>
      )}
    </Paper>
  );
}

function ActivityFeed({ items }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 4, height: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        Recent Activity
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Your latest tracked actions from the activity ledger.
      </Typography>
      {items?.length ? (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                p: 1.75,
                borderRadius: 3,
                bgcolor: "rgba(248,250,252,0.9)",
                border: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {item.title}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                {item.subtitle || "Tracked consultant activity"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                {formatDateTime(item.created_at)}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Alert severity="info">No recent activity was captured in this window yet.</Alert>
      )}
    </Paper>
  );
}

export default function ConsultantPerformanceProfile() {
  const [days, setDays] = useState(30);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");

  const loadProfile = useCallback(async (requestedDays) => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/analytics/my-performance", {
        params: { days: requestedDays },
      });

      setPayload(response.data?.data || null);
    } catch (requestError) {
      console.error("Failed to load consultant performance", requestError);
      setError("Could not load your performance profile right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile(days);
  }, [days, loadProfile]);

  const handleDownload = async () => {
    setDownloading(true);

    try {
      const response = await api.get("/analytics/my-performance/report", {
        params: { days },
        responseType: "blob",
      });

      const disposition = response.headers["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `my-performance-${days}d.csv`;
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadErr) {
      console.error("Failed to download self performance report", downloadErr);
      setDownloadError("Could not generate your performance report right now.");
    } finally {
      setDownloading(false);
    }
  };

  const member = payload?.member || {};
  const currentStatus = statusMeta(member.current_status);

  return (
    <Stack spacing={3} sx={{ p: 2, height: "100%", overflow: "auto" }}>
      <Paper
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 5,
          background: "linear-gradient(135deg, #f4fbf6 0%, #f8fafc 52%, #eff6ff 100%)",
        }}
      >
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} justifyContent="space-between">
          <Box sx={{ maxWidth: 720 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25, flexWrap: "wrap" }}>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                My Performance
              </Typography>
              <Chip
                size="small"
                label={currentStatus.label}
                sx={{
                  bgcolor: currentStatus.tone,
                  color: currentStatus.color,
                  fontWeight: 800,
                }}
              />
            </Stack>
            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 680 }}>
              This is your personal productivity profile from the unified activity ledger, including replies, calls,
              closures, campaign output, availability, and your recent activity trail.
            </Typography>
            {payload?.meta?.generated_at && (
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.4 }}>
                Last refreshed {formatDateTime(payload.meta.generated_at)}
              </Typography>
            )}
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
            <TextField
              select
              size="small"
              label="Window"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              sx={{ minWidth: 120 }}
            >
              {DAY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  Last {option} days
                </MenuItem>
              ))}
            </TextField>

            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadProfile(days)} disabled={loading}>
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}
              onClick={handleDownload}
              disabled={downloading || loading}
              sx={{ bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
            >
              My Report
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {downloadError && (
        <Alert severity="error" onClose={() => setDownloadError("")}>
          {downloadError}
        </Alert>
      )}

      {loading && (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={320} />
          <Skeleton variant="rounded" height={320} />
        </Stack>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && payload && (
        <>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                title="Messages Answered"
                value={formatNumber(member.messages_answered)}
                subtitle="Email and WhatsApp replies in this window"
                accent="#16a34a"
                icon={<BoltIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                title="Closures"
                value={formatNumber(member.closures)}
                subtitle="Resolved or closed workload items"
                accent="#ef4444"
                icon={<QueryStatsIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                title="Campaign Output"
                value={formatNumber(member.campaign_output_total)}
                subtitle="Mass email plus WhatsApp recipients sent"
                accent="#0891b2"
                icon={<CampaignIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                title="Tasks Completed"
                value={formatNumber(member.tasks_completed)}
                subtitle="Consultant-assigned tasks finished in this window"
                accent="#7c3aed"
                icon={<TaskAltIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                title="Customer Touches"
                value={formatNumber(member.customer_touches)}
                subtitle="Tracked outbound call, WhatsApp, and email touches"
                accent="#2563eb"
                icon={<PeopleAltIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                title="Presence Hours"
                value={formatHours(member.available_hours)}
                subtitle={`Automatic presence ${formatHours(member.available_hours)} total, offline ${formatHours(member.offline_hours)}`}
                accent="#0f766e"
                icon={<AccessTimeIcon />}
              />
            </Grid>
          </Grid>

          {payload.watchlist?.length ? (
            <Stack spacing={1.2}>
              {payload.watchlist.map((signal, index) => (
                <Alert key={`${signal.level}-${index}`} severity={signal.level}>
                  {signal.message}
                </Alert>
              ))}
            </Stack>
          ) : null}

          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 3, borderRadius: 4, height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Daily Productivity Trend
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                  Replies, closures, campaign output, and presence rhythm across the selected window.
                </Typography>
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={payload.trend || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="email_replies" name="Email" stroke="#2563eb" strokeWidth={3} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="whatsapp_replies" name="WhatsApp" stroke="#16a34a" strokeWidth={3} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="closures" name="Closures" stroke="#ef4444" strokeWidth={3} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="customer_touches" name="Customer Touches" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="campaign_output" name="Campaign Output" stroke="#0891b2" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="available_hours" name="Available Hours" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="offline_hours" name="Offline Hours" stroke="#64748b" strokeWidth={2.5} strokeDasharray="6 6" dot={false} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="status_switches" name="Status Switches" stroke="#7c3aed" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 3, borderRadius: 4, height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Current Status
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                  Your latest live availability state.
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircleIcon sx={{ fontSize: 12, color: currentStatus.color }} />
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {currentStatus.label}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Last active {formatDateTime(member.last_activity_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    First online {formatDateTime(member.first_online_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Last online {formatDateTime(member.last_online_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Inquiries claimed: {formatNumber(member.inquiries_claimed)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Calls handled: {formatNumber(member.calls_handled)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Status switches: {formatNumber(member.status_switches)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Customer touches: {formatNumber(member.customer_touches)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Tasks completed: {formatNumber(member.tasks_completed)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Activity events: {formatNumber(member.activity_events)}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <BreakdownBars
                title="Module Mix"
                description="Where your tracked workload was concentrated."
                items={payload.module_breakdown || []}
                accent="#2563eb"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BreakdownBars
                title="Channel Mix"
                description="Which channels were most active for you."
                items={payload.channel_breakdown || []}
                accent="#16a34a"
              />
            </Grid>
          </Grid>

          <ActivityFeed items={payload.recent_activity || []} />
        </>
      )}
    </Stack>
  );
}
